import { useState } from 'react';

import {
	Box,
	Button,
	Group,
	LoadingOverlay,
	Pagination,
	ScrollArea,
	Table,
	Text,
} from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import ReferencesEditor from '@/components/references-editor/references-editor';
import ReferencesItem from '@/components/references-item/references-item';

import { referencesApi } from '@/shared/api/references';
import { chunk } from '@/shared/helpers/utils';
import { IReference } from '@/shared/types/reference.types';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

const References: React.FC = () => {
	const [reference, setReference] = useState<IReference | null>(null);
	const [isEdit, setIsEdit] = useState(false);
	const [activePage, setPage] = useState(1);

	const { data: references, isLoading: isReferencesLoading } = useQuery({
		queryKey: ['references'],
		queryFn: referencesApi.getAllReferences,
		refetchOnMount: true,
		refetchOnReconnect: true,
		refetchOnWindowFocus: true,
	});

	const handleEdit = (reference: IReference) => {
		setReference(reference);
		setIsEdit(true);
	};

	const chunked = chunk(references ?? [], 10);

	const rows = chunked[activePage - 1]?.map((elem) => (
		<ReferencesItem key={elem.id} reference={elem} handleEdit={handleEdit} />
	));

	return (
		<>
			<LayoutRow
				title={{
					title: { children: 'Справки', size: 'h4', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100vh - 116px)' }}
			>
				{!isEdit ? (
					<Box
						pos="relative"
						style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
					>
						<LoadingOverlay
							visible={isReferencesLoading}
							zIndex={1000}
							overlayProps={{ radius: 2, blur: 2 }}
						/>
						<Group>
							<Button
								variant="filled"
								radius="lg"
								onClick={() => {
									setIsEdit(true);
									setReference(null);
								}}
							>
								<Text fw={500}>Добавить справку</Text>
							</Button>
						</Group>
						<ScrollArea h={'100vh'} pr={'md'} scrollbarSize={12} offsetScrollbars>
							<Table verticalSpacing="md" stickyHeader>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>
											<Text size="md" c="dimmed">
												Название
											</Text>
										</Table.Th>
										<Table.Th>
											<Text size="md" c="dimmed">
												Описание
											</Text>
										</Table.Th>
										<Table.Th>
											<Text size="md" c="dimmed">
												ID
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Действия
											</Text>
										</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>{rows}</Table.Tbody>
							</Table>
						</ScrollArea>
						{chunked.length > 1 && (
							<Pagination total={chunked.length} value={activePage} onChange={setPage} mt="sm" />
						)}
					</Box>
				) : (
					<ReferencesEditor
						reference={reference}
						setReference={setReference}
						setIsEdit={setIsEdit}
					/>
				)}
			</LayoutRow>
		</>
	);
};

export default References;
